// @ts-nocheck
import { useState, useRef, useEffect } from "react";
import { useDashboardApi } from "../../store/api/hooks/dashboardApiHook";
import {
  ResponsiveContainer,
  FunnelChart,
  Funnel,
  Cell,
  LabelList,
  Tooltip,
  RadialBarChart,
  RadialBar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  LineChart,
  Line,
  Legend,
  ScatterChart,
  Scatter,
  ZAxis,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import Layout from '../../components/layout';
import Header from '../../components/header';
import { downloadExcel, downloadPDF } from "../../_common/functions";
import { Dropdown, Menu } from "../../components/Dropdown";

type TimePeriod = "7D" | "30D" | "120D" | "12Mo";

const tabs = [
  "Work Requests & Tasks",
  "Team Metrics",
  "Purchasing",
  "Asset Metrics",
];

const agingColumns = ["1 Day", "2 Days", "3 Days", "4-7 Days", "7-10 Days", "10-15 Days", "15+ Days", "Total"];

const agingData = [
  { priority: "Critical", badge: { bg: "#fee2e2", text: "#dc2626" }, values: [2, 1, 3, 4, 2, 1, 3, 16] },
  { priority: "High", badge: { bg: "#ffedd5", text: "#ea580c" }, values: [4, 3, 5, 8, 4, 3, 6, 33] },
  { priority: "Medium", badge: { bg: "#fefce8", text: "#ca8a04" }, values: [6, 5, 8, 12, 7, 5, 9, 52] },
  { priority: "Low", badge: { bg: "#eff6ff", text: "#2563eb" }, values: [3, 4, 6, 9, 5, 4, 7, 38] },
];

const agingTotals = [15, 13, 22, 33, 18, 13, 25, 139];

const funnelData = [
  { name: "New", value: 210, fill: "#2B5A2E" },
  { name: "Assigned", value: 168, fill: "#3D7A3E" },
  { name: "Scheduled", value: 124, fill: "#16a34a" },
  { name: "In Progress", value: 89, fill: "#86efac" },
];

const highPriorityWRs = [
  { description: "Ventilation fan failure", facility: "Seahawk Farm LLC / Barn 3", requester: "Jake R.", asset: "Fan Cover Project", priority: "Critical" },
  { description: "Water line leak in residence", facility: "Wolfpack Farm LLC / Residence 1", requester: "Maria S.", asset: "Washer-Residence 1", priority: "High" },
  { description: "Heating system malfunction", facility: "Bobcat Farm LLC / Barn 7", requester: "Tom B.", asset: "Climate System Unit 2", priority: "Critical" },
  { description: "Generator not starting", facility: "Y24 Farm LLC / Main Facility", requester: "Carlos M.", asset: "Generator Unit 1", priority: "High" },
  { description: "Feed auger jammed", facility: "Tar Heel Farm LLC / Barn 2", requester: "Anna K.", asset: "Feed System A", priority: "High" },
  { description: "Curtain motor failure", facility: "Bugeater Farm LLC / Barn 5", requester: "Dave L.", asset: "Curtain System B", priority: "Critical" },
  { description: "Alarm panel offline", facility: "Wildcat Farm LLC / Control Room", requester: "Sam W.", asset: "Alarm Panel 3", priority: "High" },
  { description: "Cooling pad pump seized", facility: "Lancelot Farm LLC / Barn 1", requester: "Lisa P.", asset: "Evap Cooler Unit 4", priority: "High" },
  { description: "Propane regulator leak", facility: "Razorback Farm LLC / Barn 4", requester: "Jake R.", asset: "Heater System C", priority: "Critical" },
  { description: "Conveyor belt slipping", facility: "Volunteer Farm LLC / Processing", requester: "Maria S.", asset: "Belt Conveyor 2", priority: "High" },
];

const priorityBadgeStyles: Record<string, { bg: string; text: string }> = {
  Critical: { bg: "#fee2e2", text: "#dc2626" },
  High: { bg: "#ffedd5", text: "#ea580c" },
};

const radialData = [
  { name: "Low", value: 94, fill: "#2563eb" },
  { name: "Medium", value: 189, fill: "#ca8a04" },
  { name: "High", value: 112, fill: "#ea580c" },
  { name: "Critical", value: 47, fill: "#dc2626" },
];

const taskCreationTimeData = [
  { bucket: "<2 hrs", count: 18 },
  { bucket: "2-4 hrs", count: 34 },
  { bucket: "4-8 hrs", count: 45 },
  { bucket: "8-24 hrs", count: 38 },
  { bucket: "1-2 days", count: 27 },
  { bucket: "2-5 days", count: 19 },
  { bucket: ">5 days", count: 12 },
];

const taskCompletionTimeData = [
  { bucket: "<4 hrs", count: 12 },
  { bucket: "4-8 hrs", count: 28 },
  { bucket: "8-24 hrs", count: 52 },
  { bucket: "1-2 days", count: 47 },
  { bucket: "2-5 days", count: 34 },
  { bucket: "5-10 days", count: 21 },
  { bucket: ">10 days", count: 9 },
];

const renderFunnelLabel = (props: any) => {
  const { x, y, width, height, name, value } = props;
  const textColor = name === "In Progress" ? "#111827" : "#ffffff";
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      textAnchor="middle"
      dominantBaseline="central"
      fill={textColor}
      fontWeight={600}
      fontSize={13}
    >
      {name} — {value}
    </text>
  );
};

function ChartCard({ title, subtitle, rightHeader, className, children }: { title: string; subtitle: string; rightHeader?: React.ReactNode; className?: string; children: React.ReactNode }) {
  return (
    <div
      className={`bg-white rounded-lg flex flex-col ${className || ""}`}
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
    >
      <div className="px-5 pt-4 pb-3 shrink-0 flex items-start justify-between">
        <div>
          <h3 className="text-[15px] font-semibold text-[#111827]">{title}</h3>
          <p className="text-[12px] text-[#9ca3af] mt-0.5">{subtitle}</p>
        </div>
        {rightHeader && <div className="shrink-0">{rightHeader}</div>}
      </div>
      <div className="flex-1 min-h-0 px-5 pb-4" style={{ overflowY: "scroll" }}>{children}</div>
    </div>
  );
}

const priorityBadgeConfig: Record<string, { bg: string; text: string }> = {
  "Immediate mortality risk": { bg: "#fee2e2", text: "#dc2626" },
  "Immediate flock performance impact": { bg: "#ffedd5", text: "#ea580c" },
  "Ok to fix between flocks": { bg: "#fefce8", text: "#ca8a04" },
  "Preventative": { bg: "#eff6ff", text: "#2563eb" },
};

const wrPriorityColors: Record<string, string> = {
  "Immediate mortality risk": "#dc2626",
  "Immediate flock performance impact": "#ea580c",
  "Ok to fix between flocks": "#ca8a04",
  "Preventative": "#2563eb",
};

const funnelColors = ["#2B5A2E", "#3D7A3E", "#16a34a", "#86efac"];

function WRTabLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 rounded-full border-[3px] border-[#e5e7eb]" />
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-[#2B5A2E] animate-spin" />
        </div>
        <span className="text-[11px] text-[#9ca3af] font-medium">Loading data...</span>
      </div>
    </div>
  );
}

type WRTimePeriod = "1W" | "3W" | "30D" | "3Mo" | "6Mo" | "12Mo";
const wrPeriodLabels: Record<WRTimePeriod, string> = {
  "1W": "Last 1 week", "3W": "Last 3 weeks", "30D": "Last 30 days", "3Mo": "Last 3 months", "6Mo": "Last 6 months", "12Mo": "Last 12 months",
};
const wrPeriodToDays: Record<WRTimePeriod, number> = {
  "1W": 7, "3W": 21, "30D": 30, "3Mo": 90, "6Mo": 180, "12Mo": 365,
};

function WRTimeToggle({ active, onChange }: { active: WRTimePeriod; onChange: (p: WRTimePeriod) => void }) {
  const periods: WRTimePeriod[] = ["1W", "3W", "30D", "3Mo", "6Mo", "12Mo"];
  return (
    <div className="flex gap-0.5">
      {periods.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`px-2.5 py-1 text-[12px] font-medium rounded-md transition-colors ${active === p
              ? "bg-[#2B5A2E] text-white"
              : "bg-white border border-gray-300 text-gray-500 hover:bg-gray-50"
            }`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function WorkRequestsTab({ days, timePeriod }: { days: number; timePeriod: WRTimePeriod }) {
  const dashboardApi = useDashboardApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingXLS, setIsExportingXLS] = useState(false);
  const [isExportingAgingPDF, setIsExportingAgingPDF] = useState(false);
  const [isExportingAgingXLS, setIsExportingAgingXLS] = useState(false);
  const [isExportingWRPriorityPDF, setIsExportingWRPriorityPDF] = useState(false);
  const [isExportingWRPriorityXLS, setIsExportingWRPriorityXLS] = useState(false);
  const [isExportingFunnelPDF, setIsExportingFunnelPDF] = useState(false);
  const [isExportingFunnelXLS, setIsExportingFunnelXLS] = useState(false);
  const [isExportingHighPriWRPDF, setIsExportingHighPriWRPDF] = useState(false);
  const [isExportingHighPriWRXLS, setIsExportingHighPriWRXLS] = useState(false);
  const [isExportingCreationTimePDF, setIsExportingCreationTimePDF] = useState(false);
  const [isExportingCreationTimeXLS, setIsExportingCreationTimeXLS] = useState(false);
  const [isExportingCompletionTimePDF, setIsExportingCompletionTimePDF] = useState(false);
  const [isExportingCompletionTimeXLS, setIsExportingCompletionTimeXLS] = useState(false);

  useEffect(() => {
    setLoading(true);
    dashboardApi.getManagerDashboardWR(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setData(d);
        setLoading(false);
      },
      (error: any) => {
        console.error("Failed to fetch WR tab:", error);
        setLoading(false);
      }
    );
  }, [days]);

  const handleExportPDF = () => {
    setIsExportingPDF(true);
    dashboardApi.exportManagerWorkRequestsToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'manager_work_requests'); setIsExportingPDF(false); },
      () => { setIsExportingPDF(false); }
    );
  };

  const handleExportXLS = () => {
    setIsExportingXLS(true);
    dashboardApi.exportManagerWorkRequestsToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'manager_work_requests'); setIsExportingXLS(false); },
      () => { setIsExportingXLS(false); }
    );
  };

  const handleExportAgingPDF = () => {
    setIsExportingAgingPDF(true);
    dashboardApi.exportTasksAgingPriorityToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'tasks_aging_with_priority'); setIsExportingAgingPDF(false); },
      () => { setIsExportingAgingPDF(false); }
    );
  };

  const handleExportAgingXLS = () => {
    setIsExportingAgingXLS(true);
    dashboardApi.exportTasksAgingPriorityToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'tasks_aging_with_priority'); setIsExportingAgingXLS(false); },
      () => { setIsExportingAgingXLS(false); }
    );
  };

  const handleExportWRPriorityPDF = () => {
    setIsExportingWRPriorityPDF(true);
    dashboardApi.exportWRByPriorityToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'work_requests_by_priority'); setIsExportingWRPriorityPDF(false); },
      () => { setIsExportingWRPriorityPDF(false); }
    );
  };

  const handleExportWRPriorityXLS = () => {
    setIsExportingWRPriorityXLS(true);
    dashboardApi.exportWRByPriorityToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'work_requests_by_priority'); setIsExportingWRPriorityXLS(false); },
      () => { setIsExportingWRPriorityXLS(false); }
    );
  };

  const handleExportFunnelPDF = () => {
    setIsExportingFunnelPDF(true);
    dashboardApi.exportTaskFunnelToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'task_funnel'); setIsExportingFunnelPDF(false); },
      () => { setIsExportingFunnelPDF(false); }
    );
  };

  const handleExportFunnelXLS = () => {
    setIsExportingFunnelXLS(true);
    dashboardApi.exportTaskFunnelToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'task_funnel'); setIsExportingFunnelXLS(false); },
      () => { setIsExportingFunnelXLS(false); }
    );
  };

  const handleExportHighPriWRPDF = () => {
    setIsExportingHighPriWRPDF(true);
    dashboardApi.exportHighPriorityWRsToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'high_priority_work_requests'); setIsExportingHighPriWRPDF(false); },
      () => { setIsExportingHighPriWRPDF(false); }
    );
  };

  const handleExportHighPriWRXLS = () => {
    setIsExportingHighPriWRXLS(true);
    dashboardApi.exportHighPriorityWRsToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'high_priority_work_requests'); setIsExportingHighPriWRXLS(false); },
      () => { setIsExportingHighPriWRXLS(false); }
    );
  };

  const handleExportCreationTimePDF = () => {
    setIsExportingCreationTimePDF(true);
    dashboardApi.exportTaskCreationTimeToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'task_creation_time'); setIsExportingCreationTimePDF(false); },
      () => { setIsExportingCreationTimePDF(false); }
    );
  };

  const handleExportCreationTimeXLS = () => {
    setIsExportingCreationTimeXLS(true);
    dashboardApi.exportTaskCreationTimeToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'task_creation_time'); setIsExportingCreationTimeXLS(false); },
      () => { setIsExportingCreationTimeXLS(false); }
    );
  };

  const handleExportCompletionTimePDF = () => {
    setIsExportingCompletionTimePDF(true);
    dashboardApi.exportTaskCompletionTimeToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'task_completion_time'); setIsExportingCompletionTimePDF(false); },
      () => { setIsExportingCompletionTimePDF(false); }
    );
  };

  const handleExportCompletionTimeXLS = () => {
    setIsExportingCompletionTimeXLS(true);
    dashboardApi.exportTaskCompletionTimeToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'task_completion_time'); setIsExportingCompletionTimeXLS(false); },
      () => { setIsExportingCompletionTimeXLS(false); }
    );
  };

  if (loading) return <WRTabLoader />;

  const agingRows = data?.taskAging || [];
  const agingCols = ["1 Day", "2 Days", "3 Days", "4-7 Days", "7-10 Days", "10-15 Days", "15+ Days", "Total"];
  const agingTotalsCalc = agingCols.map((_, i) => agingRows.reduce((sum: number, r: any) => {
    const vals = [r.day1, r.day2, r.day3, r.day4to7, r.day7to10, r.day10to15, r.day15Plus, r.total];
    return sum + (vals[i] || 0);
  }, 0));

  const wrByPriority = (data?.wrByPriority || []).map((r: any, i: number) => ({
    name: r.priorityName,
    value: r.wrCount,
    fill: wrPriorityColors[r.priorityName] || ["#dc2626", "#ea580c", "#ca8a04", "#2563eb"][i % 4],
  }));
  const totalOpenWRs = wrByPriority.reduce((s: number, r: any) => s + r.value, 0);

  const highPriWRs = data?.highPriorityWRs || [];

  const taskFunnelData = (data?.taskFunnel || []).map((r: any, i: number) => ({
    name: r.statusName, value: r.taskCount, fill: funnelColors[i % funnelColors.length],
  }));

  const creationTimeData = (data?.taskCreationTime || []).map((r: any) => ({ bucket: r.bucket, count: r.taskCount }));
  const completionTimeData = (data?.taskCompletionTime || []).map((r: any) => ({ bucket: r.bucket, count: r.taskCount }));

  const creationMedian = creationTimeData.length > 0 ? creationTimeData.reduce((max: any, r: any) => r.count > max.count ? r : max, creationTimeData[0])?.bucket : "";
  const completionMedian = completionTimeData.length > 0 ? completionTimeData.reduce((max: any, r: any) => r.count > max.count ? r : max, completionTimeData[0])?.bucket : "";

  return (
    <div className="flex flex-col gap-5">
      <ChartCard
        title="Tasks Aging with Priority"
        subtitle="All open tasks by priority and age — excluding Closed and Cancelled (not affected by time filter)"
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportAgingPDF} disabled={isExportingAgingPDF}>
                {isExportingAgingPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportAgingXLS} disabled={isExportingAgingXLS}>
                {isExportingAgingXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th
                  style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", textAlign: "center", padding: 10, width: 100 }}
                >
                  Priority
                </th>
                {agingCols.map((col) => (
                  <th
                    key={col}
                    style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", textAlign: "center", padding: 10 }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agingRows.map((row: any, rowIdx: number) => {
                const badge = priorityBadgeConfig[row.priorityName] || { bg: "#f3f4f6", text: "#6b7280" };
                const vals = [row.day1, row.day2, row.day3, row.day4to7, row.day7to10, row.day10to15, row.day15Plus, row.total];
                return (
                  <tr key={row.priorityName} style={{ background: rowIdx % 2 === 1 ? "#fafafa" : "transparent" }}>
                    <td style={{ textAlign: "center", padding: 10 }}>
                      <span
                        style={{
                          background: badge.bg,
                          color: badge.text,
                          borderRadius: 4,
                          padding: "2px 8px",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {row.priorityName}
                      </span>
                    </td>
                    {vals.map((v: number, i: number) => (
                      <td key={i} style={{ textAlign: "center", padding: 10, fontSize: 14 }}>
                        {v}
                      </td>
                    ))}
                  </tr>
                );
              })}
              <tr style={{ background: "#f9fafb", fontWeight: 700 }}>
                <td style={{ textAlign: "center", padding: 10, fontSize: 14 }}>Total</td>
                {agingTotalsCalc.map((v: number, i: number) => (
                  <td key={i} style={{ textAlign: "center", padding: 10, fontSize: 14 }}>
                    {v}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </ChartCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:h-0 md:min-h-full flex flex-col gap-5 overflow-hidden">
          <ChartCard
            title="Work Requests by Priority"
            subtitle="All open WRs by priority level"
            rightHeader={
              <Dropdown position="end">
                <Dropdown.Button>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                    Export
                  </button>
                </Dropdown.Button>
                <Dropdown.Content>
                  <Menu onClick={handleExportWRPriorityPDF} disabled={isExportingWRPriorityPDF}>
                    {isExportingWRPriorityPDF ? "Exporting PDF..." : "PDF"}
                  </Menu>
                  <Menu onClick={handleExportWRPriorityXLS} disabled={isExportingWRPriorityXLS}>
                    {isExportingWRPriorityXLS ? "Exporting XLS..." : "Excel"}
                  </Menu>
                </Dropdown.Content>
              </Dropdown>
            }
          >
            <div className="flex flex-col">
              <div className="relative">
                <ResponsiveContainer width="100%" height={280}>
                  <RadialBarChart
                    innerRadius={30}
                    outerRadius={110}
                    data={wrByPriority}
                    startAngle={180}
                    endAngle={-180}
                  >
                    <RadialBar dataKey="value" background={{ fill: "#f3f4f6" }}>
                      {wrByPriority.map((entry: any, index: number) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </RadialBar>
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                      formatter={(value: any, name: any) => [value, name]}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div
                  className="absolute flex flex-col items-center"
                  style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }}
                >
                  <span style={{ fontSize: 24, fontWeight: 700, color: "#111827" }}>{totalOpenWRs}</span>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>Open WRs</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
                {[...wrByPriority].reverse().map((item: any) => (
                  <div key={item.name} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.fill }} />
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{item.name} {item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>

          <ChartCard
            title="New High Priority Work Requests"
            subtitle="Unscheduled high priority WRs"
            className="flex-1 min-h-0"
            rightHeader={
              <Dropdown position="end">
                <Dropdown.Button>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                    Export
                  </button>
                </Dropdown.Button>
                <Dropdown.Content>
                  <Menu onClick={handleExportHighPriWRPDF} disabled={isExportingHighPriWRPDF}>
                    {isExportingHighPriWRPDF ? "Exporting PDF..." : "PDF"}
                  </Menu>
                  <Menu onClick={handleExportHighPriWRXLS} disabled={isExportingHighPriWRXLS}>
                    {isExportingHighPriWRXLS ? "Exporting XLS..." : "Excel"}
                  </Menu>
                </Dropdown.Content>
              </Dropdown>
            }
          >
            <div className="flex-1 overflow-y-auto">
              {highPriWRs.length === 0 ? (
                <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "20px 0" }}>No high priority work requests found</p>
              ) : highPriWRs.map((wr: any, i: number) => {
                const badge = priorityBadgeConfig[wr.priorityName] || { bg: "#f3f4f6", text: "#6b7280" };
                const facilityDisplay = [wr.facilityName, wr.subFacilityName].filter(Boolean).join(" / ");
                return (
                  <div
                    key={wr.workRequestID || i}
                    className="flex items-center justify-between gap-3"
                    style={{ padding: "12px 0", borderBottom: i < highPriWRs.length - 1 ? "1px solid #f3f4f6" : "none" }}
                  >
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <p style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{wr.description}</p>
                      <p style={{ fontSize: 12, color: "#6b7280" }}>{facilityDisplay}</p>
                      <p style={{ fontSize: 12, color: "#6b7280" }}>Requester: {wr.requesterName}</p>
                    </div>
                    <span
                      className="shrink-0"
                      style={{
                        background: badge.bg,
                        color: badge.text,
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {wr.priorityName}
                    </span>
                  </div>
                );
              })}
            </div>
          </ChartCard>
        </div>

        <div className="flex flex-col gap-5">
          <ChartCard
            title="Task Funnel — All Active Tasks"
            subtitle="Current task distribution by status — excluding Closed and Cancelled"
            rightHeader={
              <Dropdown position="end">
                <Dropdown.Button>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                    Export
                  </button>
                </Dropdown.Button>
                <Dropdown.Content>
                  <Menu onClick={handleExportFunnelPDF} disabled={isExportingFunnelPDF}>
                    {isExportingFunnelPDF ? "Exporting PDF..." : "PDF"}
                  </Menu>
                  <Menu onClick={handleExportFunnelXLS} disabled={isExportingFunnelXLS}>
                    {isExportingFunnelXLS ? "Exporting XLS..." : "Excel"}
                  </Menu>
                </Dropdown.Content>
              </Dropdown>
            }
          >
            <div className="flex flex-col">
              <ResponsiveContainer width="100%" height={320}>
                <FunnelChart>
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(value: any, name: any) => [value, name]}
                  />
                  <Funnel dataKey="value" data={taskFunnelData} isAnimationActive>
                    {taskFunnelData.map((entry: any, index: number) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                    <LabelList content={renderFunnelLabel} />
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
                {taskFunnelData.map((stage: any) => (
                  <div key={stage.name} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: stage.fill }} />
                    <span className="text-[11px] text-[#6b7280]">{stage.name}: {stage.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>

          <ChartCard
            title="Task Creation Time"
            subtitle="Hours from WR creation to Task creation"
            rightHeader={
              <Dropdown position="end">
                <Dropdown.Button>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                    Export
                  </button>
                </Dropdown.Button>
                <Dropdown.Content>
                  <Menu onClick={handleExportCreationTimePDF} disabled={isExportingCreationTimePDF}>
                    {isExportingCreationTimePDF ? "Exporting PDF..." : "PDF"}
                  </Menu>
                  <Menu onClick={handleExportCreationTimeXLS} disabled={isExportingCreationTimeXLS}>
                    {isExportingCreationTimeXLS ? "Exporting XLS..." : "Excel"}
                  </Menu>
                </Dropdown.Content>
              </Dropdown>
            }
          >
            <div className="flex flex-col">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={creationTimeData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Tasks", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(value: any, name: any, props: any) => [`${value} tasks`, props.payload.bucket]}
                    labelFormatter={() => ""}
                  />
                  <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>Most common: {creationMedian}</p>
            </div>
          </ChartCard>

          <ChartCard
            title="Task Completion Time"
            subtitle="Hours from Task creation to Closed-Complete"
            rightHeader={
              <Dropdown position="end">
                <Dropdown.Button>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                    Export
                  </button>
                </Dropdown.Button>
                <Dropdown.Content>
                  <Menu onClick={handleExportCompletionTimePDF} disabled={isExportingCompletionTimePDF}>
                    {isExportingCompletionTimePDF ? "Exporting PDF..." : "PDF"}
                  </Menu>
                  <Menu onClick={handleExportCompletionTimeXLS} disabled={isExportingCompletionTimeXLS}>
                    {isExportingCompletionTimeXLS ? "Exporting XLS..." : "Excel"}
                  </Menu>
                </Dropdown.Content>
              </Dropdown>
            }
          >
            <div className="flex flex-col">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={completionTimeData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Tasks", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(value: any, name: any, props: any) => [`${value} tasks`, props.payload.bucket]}
                    labelFormatter={() => ""}
                  />
                  <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>Most common: {completionMedian}</p>
            </div>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

const rmUsers = [
  { key: "Jake R.", color: "#16a34a" },
  { key: "Maria S.", color: "#2563eb" },
  { key: "Tom B.", color: "#d97706" },
  { key: "Carlos M.", color: "#7c3aed" },
  { key: "Anna K.", color: "#dc2626" },
  { key: "Dave L.", color: "#0891b2" },
];

const rmBase = [
  { facility: "Seahawk", "Jake R.": 12, "Maria S.": 8, "Tom B.": 10, "Carlos M.": 14, "Anna K.": 6, "Dave L.": 9 },
  { facility: "Bugeater", "Jake R.": 9, "Maria S.": 11, "Tom B.": 7, "Carlos M.": 8, "Anna K.": 10, "Dave L.": 6 },
  { facility: "Y24", "Jake R.": 6, "Maria S.": 5, "Tom B.": 8, "Carlos M.": 7, "Anna K.": 4, "Dave L.": 11 },
  { facility: "Bobcat", "Jake R.": 14, "Maria S.": 9, "Tom B.": 12, "Carlos M.": 10, "Anna K.": 8, "Dave L.": 7 },
  { facility: "Wolfpack", "Jake R.": 8, "Maria S.": 7, "Tom B.": 6, "Carlos M.": 9, "Anna K.": 5, "Dave L.": 8 },
  { facility: "Tar Heel", "Jake R.": 10, "Maria S.": 12, "Tom B.": 9, "Carlos M.": 11, "Anna K.": 7, "Dave L.": 10 },
];

function TimeOnFarmChart({ title, subtitle, users, baseData, days }: {
  title: string; subtitle: string; users: { key: string; color: string }[];
  baseData: Record<string, any>[]; days: number;
}) {
  const dashboardApi = useDashboardApi();
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingXLS, setIsExportingXLS] = useState(false);

  const handleExportPDF = () => {
    setIsExportingPDF(true);
    dashboardApi.exportManagerHoursByFacilityToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'time_on_farm'); setIsExportingPDF(false); },
      () => { setIsExportingPDF(false); }
    );
  };

  const handleExportXLS = () => {
    setIsExportingXLS(true);
    dashboardApi.exportManagerHoursByFacilityToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'time_on_farm'); setIsExportingXLS(false); },
      () => { setIsExportingXLS(false); }
    );
  };

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      rightHeader={
        <Dropdown position="end">
          <Dropdown.Button>
            <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
              Export
            </button>
          </Dropdown.Button>
          <Dropdown.Content>
            <Menu onClick={handleExportPDF} disabled={isExportingPDF}>
              {isExportingPDF ? "Exporting PDF..." : "PDF"}
            </Menu>
            <Menu onClick={handleExportXLS} disabled={isExportingXLS}>
              {isExportingXLS ? "Exporting XLS..." : "Excel"}
            </Menu>
          </Dropdown.Content>
        </Dropdown>
      }
    >
      <div className="flex flex-col">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={baseData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="facility" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Hours", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
                const sorted = [...payload].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
                return (
                  <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                    {sorted.map((entry) => (
                      <p key={entry.dataKey as string} style={{ color: entry.color, margin: "2px 0" }}>
                        {entry.dataKey}: {entry.value} hrs
                      </p>
                    ))}
                    <p style={{ fontWeight: 600, marginTop: 4, borderTop: "1px solid #e5e7eb", paddingTop: 4 }}>
                      Total: {Math.round(total * 10) / 10} hrs
                    </p>
                  </div>
                );
              }}
            />
            {users.map((u, i) => (
              <Bar key={u.key} dataKey={u.key} stackId="a" fill={u.color} radius={i === users.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
          {users.map((u) => (
            <div key={u.key} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: u.color }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>{u.key}</span>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}

const completedTasksUsers = [
  { key: "Jake R.", color: "#16a34a", base: 28 },
  { key: "Maria S.", color: "#2563eb", base: 34 },
  { key: "Tom B.", color: "#d97706", base: 22 },
  { key: "Carlos M.", color: "#7c3aed", base: 41 },
  { key: "Anna K.", color: "#dc2626", base: 18 },
  { key: "Dave L.", color: "#0891b2", base: 26 },
  { key: "Mike T.", color: "#f59e0b", base: 31 },
  { key: "Sarah L.", color: "#10b981", base: 29 },
];

const loggedHoursData: Record<TimePeriod, { name: string; hours: number }[]> = {
  "7D": [
    { name: "Jake R.", hours: 4 }, { name: "Maria S.", hours: 6 }, { name: "Tom B.", hours: 3 },
    { name: "Carlos M.", hours: 7 }, { name: "Anna K.", hours: 2 }, { name: "Dave L.", hours: 5 },
  ],
  "30D": [
    { name: "Jake R.", hours: 18 }, { name: "Maria S.", hours: 24 }, { name: "Tom B.", hours: 14 },
    { name: "Carlos M.", hours: 28 }, { name: "Anna K.", hours: 11 }, { name: "Dave L.", hours: 20 },
  ],
  "120D": [
    { name: "Jake R.", hours: 72 }, { name: "Maria S.", hours: 96 }, { name: "Tom B.", hours: 58 },
    { name: "Carlos M.", hours: 110 }, { name: "Anna K.", hours: 44 }, { name: "Dave L.", hours: 81 },
  ],
  "12Mo": [
    { name: "Jake R.", hours: 280 }, { name: "Maria S.", hours: 370 }, { name: "Tom B.", hours: 220 },
    { name: "Carlos M.", hours: 430 }, { name: "Anna K.", hours: 170 }, { name: "Dave L.", hours: 310 },
  ],
};

function TimePeriodToggle({ period, setPeriod }: {
  period: TimePeriod; setPeriod: (p: TimePeriod) => void;
}) {
  return (
    <div className="flex gap-1">
      {(["7D", "30D", "120D", "12Mo"] as TimePeriod[]).map((p) => (
        <button
          key={p}
          onClick={() => setPeriod(p)}
          aria-pressed={period === p}
          style={{
            padding: "4px 10px", fontSize: 12, fontWeight: 500, borderRadius: 4, cursor: "pointer",
            background: period === p ? "#2B5A2E" : "white",
            color: period === p ? "white" : "#6b7280",
            border: period === p ? "1px solid #2B5A2E" : "1px solid #e5e7eb",
          }}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function CompletedTasksDonut() {
  const [period, setPeriod] = useState<TimePeriod>("30D");
  const multiplier = period === "7D" ? 0.25 : period === "120D" ? 4 : period === "12Mo" ? 12 : 1;
  const data = completedTasksUsers.map((u) => ({
    name: u.key, value: Math.round(u.base * multiplier), color: u.color,
  }));
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <ChartCard
      title="Completed Tasks by Employee"
      subtitle="Closed-Complete tasks by user"
      rightHeader={<TimePeriodToggle period={period} setPeriod={setPeriod} />}
    >
      <div className="flex items-center justify-center gap-4">
        <div className="relative shrink-0" style={{ width: 240, height: 260 }}>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100}>
                {data.map((d, i) => <Cell key={i} fill={d.color} stroke="white" strokeWidth={2} />)}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0];
                  const pct = total > 0 ? ((Number(p.value) / total) * 100).toFixed(1) : "0";
                  return (
                    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                      <p style={{ fontWeight: 600 }}>{p.name}</p>
                      <p>{p.value} tasks ({pct}%)</p>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{total}</span>
            <span style={{ fontSize: 11, color: "#6b7280" }}>Tasks</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          {data.map((d) => (
            <div key={d.name} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: d.color }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>{d.name} — {d.value}</span>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}

function LoggedHoursChart() {
  const [period, setPeriod] = useState<TimePeriod>("30D");
  const data = loggedHoursData[period];

  return (
    <ChartCard
      title="Logged Hours by Employee — In Progress"
      subtitle="Hours logged on tasks with status = In Progress"
      rightHeader={<TimePeriodToggle period={period} setPeriod={setPeriod} />}
    >
      <div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Hours", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
              formatter={(value: any) => [`${value} hrs`, "Hours logged"]}
            />
            <Bar dataKey="hours" fill="#2B5A2E" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

const pmReactiveCount = [
  { week: "Wk 1", PM: 8, Reactive: 22 }, { week: "Wk 2", PM: 10, Reactive: 19 },
  { week: "Wk 3", PM: 7, Reactive: 25 }, { week: "Wk 4", PM: 12, Reactive: 18 },
  { week: "Wk 5", PM: 9, Reactive: 21 }, { week: "Wk 6", PM: 11, Reactive: 20 },
  { week: "Wk 7", PM: 13, Reactive: 17 }, { week: "Wk 8", PM: 10, Reactive: 23 },
  { week: "Wk 9", PM: 14, Reactive: 16 }, { week: "Wk 10", PM: 11, Reactive: 19 },
  { week: "Wk 11", PM: 15, Reactive: 15 }, { week: "Wk 12", PM: 13, Reactive: 18 },
];

function PMReactiveChart() {
  const [metric, setMetric] = useState<"Count" | "Hours">("Count");
  const data = metric === "Count"
    ? pmReactiveCount
    : pmReactiveCount.map((r) => ({
      week: r.week,
      PM: Math.round(r.PM * 2.4 * 10) / 10,
      Reactive: Math.round(r.Reactive * 1.8 * 10) / 10,
    }));
  const unit = metric === "Count" ? "tasks" : "hrs";

  return (
    <ChartCard
      title="PM vs Reactive Tasks"
      subtitle="Preventive Maintenance tasks vs Work Request tasks — last 12 weeks"
      rightHeader={
        <div className="flex">
          {(["Count", "Hours"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              aria-pressed={metric === m}
              style={{
                padding: "4px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer",
                background: metric === m ? "#2B5A2E" : "white",
                color: metric === m ? "white" : "#6b7280",
                border: metric === m ? "1px solid #2B5A2E" : "1px solid #e5e7eb",
                borderRadius: m === "Count" ? "4px 0 0 4px" : "0 4px 4px 0",
              }}
            >
              {m}
            </button>
          ))}
        </div>
      }
    >
      <div className="flex flex-col">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: metric === "Count" ? "Tasks" : "Hours", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const pm = Number(payload.find((p) => p.dataKey === "PM")?.value || 0);
                const re = Number(payload.find((p) => p.dataKey === "Reactive")?.value || 0);
                return (
                  <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                    <p style={{ color: "#16a34a", margin: "2px 0" }}>PM: {pm} {unit}</p>
                    <p style={{ color: "#3b82f6", margin: "2px 0" }}>Reactive: {re} {unit}</p>
                    <p style={{ fontWeight: 600, marginTop: 4, borderTop: "1px solid #e5e7eb", paddingTop: 4 }}>
                      Total: {Math.round((pm + re) * 10) / 10} {unit}
                    </p>
                  </div>
                );
              }}
            />
            <Bar dataKey="PM" stackId="a" fill="#16a34a" />
            <Bar dataKey="Reactive" stackId="a" fill="#3b82f6" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-x-6 gap-y-1 justify-center pt-2">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: "#16a34a" }} />
            <span style={{ fontSize: 12, color: "#6b7280" }}>PM (Preventive Maintenance)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: "#3b82f6" }} />
            <span style={{ fontSize: 12, color: "#6b7280" }}>Reactive (Work Request)</span>
          </div>
        </div>
      </div>
    </ChartCard>
  );
}

const DONUT_COLORS = ["#16a34a", "#2563eb", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#f59e0b", "#10b981", "#ec4899", "#6366f1", "#14b8a6", "#f43f5e"];

function transformTimeOnFarm(rows: any[]): Record<string, any>[] {
  const facilityMap = new Map<string, Record<string, any>>();
  rows.forEach((r) => {
    if (!facilityMap.has(r.facilityName)) facilityMap.set(r.facilityName, { facility: r.facilityName });
    facilityMap.get(r.facilityName)![r.userName] = Math.round(r.hours * 10) / 10;
  });
  return Array.from(facilityMap.values());
}

function extractTimeOnFarmUsers(rows: any[], colors: string[]): { key: string; color: string }[] {
  const seen = new Map<string, { key: string; color: string }>();
  rows.forEach((r) => {
    if (!seen.has(r.userName)) {
      seen.set(r.userName, { key: r.userName, color: colors[seen.size % colors.length] });
    }
  });
  return Array.from(seen.values());
}

function TeamMetricsTab({ days }: { days: number }) {
  const dashboardApi = useDashboardApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [timeOnFarmRaw, setTimeOnFarmRaw] = useState<any[]>([]);
  const [timeOnFarmLoading, setTimeOnFarmLoading] = useState(true);
  const [isExportingCompletedTasksPDF, setIsExportingCompletedTasksPDF] = useState(false);
  const [isExportingCompletedTasksXLS, setIsExportingCompletedTasksXLS] = useState(false);
  const [isExportingLoggedHoursPDF, setIsExportingLoggedHoursPDF] = useState(false);
  const [isExportingLoggedHoursXLS, setIsExportingLoggedHoursXLS] = useState(false);
  const [isExportingPMReactivePDF, setIsExportingPMReactivePDF] = useState(false);
  const [isExportingPMReactiveXLS, setIsExportingPMReactiveXLS] = useState(false);

  useEffect(() => {
    setLoading(true);
    dashboardApi.getManagerDashboardTeam(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setData(d);
        setLoading(false);
      },
      (error: any) => { console.error("Failed to fetch team metrics:", error); setLoading(false); }
    );
  }, [days]);

  useEffect(() => {
    setTimeOnFarmLoading(true);
    dashboardApi.getManagerHoursByFacility(
      { Days: days },
      (response: any) => {
        const rows = response?.data || response || [];
        setTimeOnFarmRaw(Array.isArray(rows) ? rows : []);
        setTimeOnFarmLoading(false);
      },
      (error: any) => {
        console.error("Failed to fetch time on farm data:", error);
        setTimeOnFarmRaw([]);
        setTimeOnFarmLoading(false);
      }
    );
  }, [days]);

  const handleExportCompletedTasksPDF = () => {
    setIsExportingCompletedTasksPDF(true);
    dashboardApi.exportCompletedTasksByEmployeeToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'completed_tasks_by_employee'); setIsExportingCompletedTasksPDF(false); },
      () => { setIsExportingCompletedTasksPDF(false); }
    );
  };
  const handleExportCompletedTasksXLS = () => {
    setIsExportingCompletedTasksXLS(true);
    dashboardApi.exportCompletedTasksByEmployeeToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'completed_tasks_by_employee'); setIsExportingCompletedTasksXLS(false); },
      () => { setIsExportingCompletedTasksXLS(false); }
    );
  };
  const handleExportLoggedHoursPDF = () => {
    setIsExportingLoggedHoursPDF(true);
    dashboardApi.exportLoggedHoursByEmployeeToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'logged_hours_by_employee'); setIsExportingLoggedHoursPDF(false); },
      () => { setIsExportingLoggedHoursPDF(false); }
    );
  };
  const handleExportLoggedHoursXLS = () => {
    setIsExportingLoggedHoursXLS(true);
    dashboardApi.exportLoggedHoursByEmployeeToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'logged_hours_by_employee'); setIsExportingLoggedHoursXLS(false); },
      () => { setIsExportingLoggedHoursXLS(false); }
    );
  };
  const handleExportPMReactivePDF = () => {
    setIsExportingPMReactivePDF(true);
    dashboardApi.exportPMReactiveToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'pm_vs_reactive'); setIsExportingPMReactivePDF(false); },
      () => { setIsExportingPMReactivePDF(false); }
    );
  };
  const handleExportPMReactiveXLS = () => {
    setIsExportingPMReactiveXLS(true);
    dashboardApi.exportPMReactiveToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'pm_vs_reactive'); setIsExportingPMReactiveXLS(false); },
      () => { setIsExportingPMReactiveXLS(false); }
    );
  };

  if (loading) return <WRTabLoader />;

  // Completed Tasks donut
  const completedTasks = (data?.completedTasks || []).map((r: any, i: number) => ({
    name: r.userName?.trim(), value: r.taskCount, color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));
  const totalCompleted = completedTasks.reduce((s: number, r: any) => s + r.value, 0);

  // Logged Hours bar
  const loggedHours = (data?.loggedHours || []).map((r: any) => ({
    name: r.userName?.trim(), hours: Math.round((r.hours || 0) * 10) / 10,
  }));

  // PM vs Reactive
  const pmReactiveData = (data?.pmReactive || []).map((r: any) => ({
    week: r.bucketLabel, PM: r.pm || 0, Reactive: r.reactive || 0,
  }));

  // Time on Farm data
  const timeOnFarmData = transformTimeOnFarm(timeOnFarmRaw);
  const timeOnFarmUsers = extractTimeOnFarmUsers(timeOnFarmRaw, DONUT_COLORS);

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1">
        {timeOnFarmLoading ? (
          <WRTabLoader />
        ) : timeOnFarmData.length === 0 ? (
          <ChartCard
            title="Time on Farm"
            subtitle="Physical check-in to check-out hours at each facility by user and role"
            testId="chart-time-on-farm"
          >
            <div className="flex flex-col items-center justify-center" style={{ height: 320 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
              <p style={{ fontSize: 14, color: "#9ca3af", marginTop: 12 }} data-testid="text-no-data">No data to display</p>
            </div>
          </ChartCard>
        ) : (
          <TimeOnFarmChart
            title="Time on Farm"
            subtitle="Physical check-in to check-out hours at each facility by user and role"
            users={timeOnFarmUsers}
            baseData={timeOnFarmData}
            days={days}
          />
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Completed Tasks by Employee — Donut */}
        <ChartCard title="Completed Tasks by Employee" subtitle="Closed-Complete tasks by user"
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportCompletedTasksPDF} disabled={isExportingCompletedTasksPDF}>
                  {isExportingCompletedTasksPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportCompletedTasksXLS} disabled={isExportingCompletedTasksXLS}>
                  {isExportingCompletedTasksXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {completedTasks.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No completed tasks in this period</p>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="relative shrink-0" style={{ width: 240, height: 260 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={completedTasks} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100}>
                      {completedTasks.map((d: any, i: number) => <Cell key={i} fill={d.color} stroke="white" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0];
                        const pct = totalCompleted > 0 ? ((Number(p.value) / totalCompleted) * 100).toFixed(1) : "0";
                        return (
                          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                            <p style={{ fontWeight: 600 }}>{p.name}</p>
                            <p>{p.value} tasks ({pct}%)</p>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{totalCompleted}</span>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>Tasks</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                {completedTasks.map((d: any) => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: d.color }} />
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{d.name} — {d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        {/* Logged Hours by Employee */}
        <ChartCard title="Logged Hours by Employee" subtitle="Hours in progress per user"
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportLoggedHoursPDF} disabled={isExportingLoggedHoursPDF}>
                  {isExportingLoggedHoursPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportLoggedHoursXLS} disabled={isExportingLoggedHoursXLS}>
                  {isExportingLoggedHoursXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {loggedHours.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No logged hours in this period</p>
          ) : (
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={loggedHours} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Hours", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(value: any) => [`${value} hrs`, "Hours logged"]}
                  />
                  <Bar dataKey="hours" fill="#2B5A2E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>
      </div>

      {/* PM vs Reactive Tasks */}
      <ChartCard title="PM vs Reactive Tasks" subtitle="Preventive Maintenance (from Scheduler) vs Reactive (all other) tasks"
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportPMReactivePDF} disabled={isExportingPMReactivePDF}>
                {isExportingPMReactivePDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportPMReactiveXLS} disabled={isExportingPMReactiveXLS}>
                {isExportingPMReactiveXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        {pmReactiveData.length === 0 ? (
          <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No data in this period</p>
        ) : (
          <div className="flex flex-col">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={pmReactiveData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#6b7280" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Tasks", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const pm = Number(payload.find((p) => p.dataKey === "PM")?.value || 0);
                    const re = Number(payload.find((p) => p.dataKey === "Reactive")?.value || 0);
                    return (
                      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                        <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                        <p style={{ color: "#16a34a", margin: "2px 0" }}>PM: {pm} tasks</p>
                        <p style={{ color: "#3b82f6", margin: "2px 0" }}>Reactive: {re} tasks</p>
                        <p style={{ fontWeight: 600, marginTop: 4, borderTop: "1px solid #e5e7eb", paddingTop: 4 }}>
                          Total: {pm + re} tasks
                        </p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="PM" stackId="a" fill="#16a34a" />
                <Bar dataKey="Reactive" stackId="a" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-6 gap-y-1 justify-center pt-2">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: "#16a34a" }} />
                <span style={{ fontSize: 12, color: "#6b7280" }}>PM (Preventive Maintenance)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: "#3b82f6" }} />
                <span style={{ fontSize: 12, color: "#6b7280" }}>Reactive</span>
              </div>
            </div>
          </div>
        )}
      </ChartCard>
    </div>
  );
}

const purchasingMethodData = [
  { week: "Wk 1", Emergency: 35, StockPick: 65 }, { week: "Wk 2", Emergency: 28, StockPick: 72 },
  { week: "Wk 3", Emergency: 40, StockPick: 60 }, { week: "Wk 4", Emergency: 32, StockPick: 68 },
  { week: "Wk 5", Emergency: 38, StockPick: 62 }, { week: "Wk 6", Emergency: 25, StockPick: 75 },
  { week: "Wk 7", Emergency: 30, StockPick: 70 }, { week: "Wk 8", Emergency: 36, StockPick: 64 },
  { week: "Wk 9", Emergency: 29, StockPick: 71 }, { week: "Wk 10", Emergency: 33, StockPick: 67 },
  { week: "Wk 11", Emergency: 27, StockPick: 73 }, { week: "Wk 12", Emergency: 31, StockPick: 69 },
];

const purchasingSpendData = [
  { month: "Jan 2025", emergency: 4820, stock: 9240, total: 14060 },
  { month: "Feb 2025", emergency: 3650, stock: 8780, total: 12430 },
  { month: "Mar 2025", emergency: 5210, stock: 10120, total: 15330 },
  { month: "Apr 2025", emergency: 4180, stock: 9560, total: 13740 },
  { month: "May 2025", emergency: 6340, stock: 11280, total: 17620 },
  { month: "Jun 2025", emergency: 3920, stock: 8940, total: 12860 },
  { month: "Jul 2025", emergency: 5780, stock: 10640, total: 16420 },
  { month: "Aug 2025", emergency: 4460, stock: 9380, total: 13840 },
  { month: "Sep 2025", emergency: 6120, stock: 11560, total: 17680 },
  { month: "Oct 2025", emergency: 5290, stock: 10840, total: 16130 },
  { month: "Nov 2025", emergency: 4710, stock: 9720, total: 14430 },
  { month: "Dec 2025", emergency: 5960, stock: 11280, total: 17240 },
];

const spendTotals = {
  emergency: purchasingSpendData.reduce((s, r) => s + r.emergency, 0),
  stock: purchasingSpendData.reduce((s, r) => s + r.stock, 0),
  total: purchasingSpendData.reduce((s, r) => s + r.total, 0),
};

function fmt$(n: number) {
  return "$" + n.toLocaleString();
}

function PurchasingTab({ days }: { days: number }) {
  const dashboardApi = useDashboardApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isExportingMethodPDF, setIsExportingMethodPDF] = useState(false);
  const [isExportingMethodXLS, setIsExportingMethodXLS] = useState(false);
  const [isExportingSpendPDF, setIsExportingSpendPDF] = useState(false);
  const [isExportingSpendXLS, setIsExportingSpendXLS] = useState(false);
  const [isExportingGoodsOnOrderPDF, setIsExportingGoodsOnOrderPDF] = useState(false);
  const [isExportingGoodsOnOrderXLS, setIsExportingGoodsOnOrderXLS] = useState(false);
  const [isExportingLowInventoryPDF, setIsExportingLowInventoryPDF] = useState(false);
  const [isExportingLowInventoryXLS, setIsExportingLowInventoryXLS] = useState(false);

  useEffect(() => {
    setLoading(true);
    dashboardApi.getManagerDashboardPurchasing(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setData(d);
        setLoading(false);
      },
      (error: any) => { console.error("Failed to fetch purchasing:", error); setLoading(false); }
    );
  }, [days]);

  if (loading) return <WRTabLoader />;

  const goodsOnOrder = data?.goodsOnOrder || [];
  const lowInventory = data?.lowInventory || [];
  const criticalCount = lowInventory.filter((r: any) => r.stockStatus === "Critical").length;
  const lowCount = lowInventory.filter((r: any) => r.stockStatus === "Low").length;

  const methodData: any[] = data?.purchasingMethod || [];
  const spendData: any[] = data?.purchasingSpend || [];

  const numWeeks = Math.min(Math.ceil(days / 7), 12);
  const numMonths = Math.min(Math.ceil(days / 30), 12);

  const avgEmergency = methodData.length > 0
    ? Math.round(methodData.reduce((s: number, r: any) => s + Number(r.emergency || 0), 0) / methodData.length)
    : 0;

  const spendTotalsLive = {
    emergency: spendData.reduce((s: number, r: any) => s + Number(r.emergencySpend || 0), 0),
    stock:     spendData.reduce((s: number, r: any) => s + Number(r.stockSpend || 0), 0),
    total:     spendData.reduce((s: number, r: any) => s + Number(r.totalSpend || 0), 0),
  };

  const handleExportMethodPDF = () => {
    setIsExportingMethodPDF(true);
    dashboardApi.exportPurchasingMethodToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'purchasing_method'); setIsExportingMethodPDF(false); },
      () => { setIsExportingMethodPDF(false); }
    );
  };
  const handleExportMethodXLS = () => {
    setIsExportingMethodXLS(true);
    dashboardApi.exportPurchasingMethodToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'purchasing_method'); setIsExportingMethodXLS(false); },
      () => { setIsExportingMethodXLS(false); }
    );
  };

  const handleExportSpendPDF = () => {
    setIsExportingSpendPDF(true);
    dashboardApi.exportPurchasingSpendToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'purchasing_spend'); setIsExportingSpendPDF(false); },
      () => { setIsExportingSpendPDF(false); }
    );
  };
  const handleExportSpendXLS = () => {
    setIsExportingSpendXLS(true);
    dashboardApi.exportPurchasingSpendToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'purchasing_spend'); setIsExportingSpendXLS(false); },
      () => { setIsExportingSpendXLS(false); }
    );
  };

  const handleExportGoodsOnOrderPDF = () => {
    setIsExportingGoodsOnOrderPDF(true);
    dashboardApi.exportGoodsOnOrderToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'goods_on_order'); setIsExportingGoodsOnOrderPDF(false); },
      () => { setIsExportingGoodsOnOrderPDF(false); }
    );
  };
  const handleExportGoodsOnOrderXLS = () => {
    setIsExportingGoodsOnOrderXLS(true);
    dashboardApi.exportGoodsOnOrderToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'goods_on_order'); setIsExportingGoodsOnOrderXLS(false); },
      () => { setIsExportingGoodsOnOrderXLS(false); }
    );
  };

  const handleExportLowInventoryPDF = () => {
    setIsExportingLowInventoryPDF(true);
    dashboardApi.exportLowInventoryToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'low_inventory'); setIsExportingLowInventoryPDF(false); },
      () => { setIsExportingLowInventoryPDF(false); }
    );
  };
  const handleExportLowInventoryXLS = () => {
    setIsExportingLowInventoryXLS(true);
    dashboardApi.exportLowInventoryToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'low_inventory'); setIsExportingLowInventoryXLS(false); },
      () => { setIsExportingLowInventoryXLS(false); }
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <ChartCard
        title="Purchasing Method"
        subtitle={`Emergency Part Purchase vs Stock Pick — last ${numWeeks} week${numWeeks !== 1 ? 's' : ''}`}
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportMethodPDF} disabled={isExportingMethodPDF}>
                {isExportingMethodPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportMethodXLS} disabled={isExportingMethodXLS}>
                {isExportingMethodXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        {methodData.length === 0 ? (
          <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No purchase order data available</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={methodData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} />
                <Tooltip
                  formatter={(value: any, name: string) => [`${value}%`, name === "emergency" ? "Emergency Part Purchase" : "Stock Pick"]}
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                />
                <Legend
                  iconType="square"
                  iconSize={10}
                  formatter={(value) => <span style={{ fontSize: 12, color: "#374151" }}>{value === "emergency" ? "Emergency Part Purchase" : "Stock Pick"}</span>}
                />
                <Bar dataKey="emergency" stackId="a" fill="#dc2626" radius={[0, 0, 0, 0]} />
                <Bar dataKey="stockPick" stackId="a" fill="#16a34a" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Avg Emergency Purchase: <strong>{avgEmergency}%</strong> over last {numWeeks} week{numWeeks !== 1 ? 's' : ''}
            </p>
          </>
        )}
      </ChartCard>

      <ChartCard
        title="Purchasing Spend"
        subtitle={`Monthly spend breakdown — last ${numMonths} month${numMonths !== 1 ? 's' : ''}`}
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportSpendPDF} disabled={isExportingSpendPDF}>
                {isExportingSpendPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportSpendXLS} disabled={isExportingSpendXLS}>
                {isExportingSpendXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        {spendData.length === 0 ? (
          <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No spend data available</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {["Month", "Emergency Purchases", "Stock Purchases", "Total"].map((h) => (
                    <th key={h} style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 14px", textAlign: "left", borderBottom: "1px solid #f3f4f6" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {spendData.map((row: any, i: number) => (
                  <tr key={row.month} style={{ background: i % 2 === 1 ? "#fff5f5" : "white" }}>
                    <td style={{ fontSize: 13, padding: "10px 14px", color: "#111827" }}>{row.month}</td>
                    <td style={{ fontSize: 13, padding: "10px 14px", color: "#dc2626" }}>{fmt$(Number(row.emergencySpend || 0))}</td>
                    <td style={{ fontSize: 13, padding: "10px 14px", color: "#111827" }}>{fmt$(Number(row.stockSpend || 0))}</td>
                    <td style={{ fontSize: 13, padding: "10px 14px", fontWeight: 700, color: "#111827" }}>{fmt$(Number(row.totalSpend || 0))}</td>
                  </tr>
                ))}
                <tr style={{ background: "#f9fafb", borderTop: "2px solid #e5e7eb" }}>
                  <td style={{ fontSize: 13, padding: "10px 14px", fontWeight: 700, color: "#111827" }}>Total</td>
                  <td style={{ fontSize: 13, padding: "10px 14px", fontWeight: 700, color: "#dc2626" }}>{fmt$(spendTotalsLive.emergency)}</td>
                  <td style={{ fontSize: 13, padding: "10px 14px", fontWeight: 700, color: "#111827" }}>{fmt$(spendTotalsLive.stock)}</td>
                  <td style={{ fontSize: 13, padding: "10px 14px", fontWeight: 700, color: "#111827" }}>{fmt$(spendTotalsLive.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ChartCard
          title="Goods on Order"
          subtitle="PO line items not yet received"
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportGoodsOnOrderPDF} disabled={isExportingGoodsOnOrderPDF}>
                  {isExportingGoodsOnOrderPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportGoodsOnOrderXLS} disabled={isExportingGoodsOnOrderXLS}>
                  {isExportingGoodsOnOrderXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {goodsOnOrder.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No outstanding purchase orders</p>
          ) : (
            <>
              <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Part Name", "Qty", "Unit Price", "Total", "Vendor", "Needed By"].map((h) => (
                        <th key={h} style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 12px", textAlign: "left", position: "sticky", top: 0 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {goodsOnOrder.map((row: any, i: number) => (
                      <tr key={row.purchaseOrderID + '-' + row.partsID} style={{ background: i % 2 === 1 ? "#fafafa" : "white" }}>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.partsName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.quantity}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>${Number(row.unitPrice || 0).toLocaleString()}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", fontWeight: 600, color: "#111827" }}>${Number(row.totalAmount || 0).toLocaleString()}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.vendorName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#6b7280" }}>{row.requiredByDate ? new Date(row.requiredByDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 12 }}>
                <span style={{ fontSize: 12, color: "#6b7280" }}>{goodsOnOrder.length} item{goodsOnOrder.length !== 1 ? "s" : ""} on order</span>
              </div>
            </>
          )}
        </ChartCard>

        <ChartCard
          title="Low Inventory"
          subtitle="Parts at or below reorder point"
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportLowInventoryPDF} disabled={isExportingLowInventoryPDF}>
                  {isExportingLowInventoryPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportLowInventoryXLS} disabled={isExportingLowInventoryXLS}>
                  {isExportingLowInventoryXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {lowInventory.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>All parts above reorder point</p>
          ) : (
            <>
              <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Part Name", "Qty on Hand", "Min Qty", "Facility", "Status"].map((h) => (
                        <th key={h} style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 12px", textAlign: "left", position: "sticky", top: 0 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lowInventory.map((row: any, i: number) => (
                      <tr key={row.partsID} style={{ background: i % 2 === 1 ? "#fafafa" : "white" }}>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.partsName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.qtyOnHand}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.minStock}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#6b7280" }}>{row.facilityName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px" }}>
                          <span
                            style={{
                              background: row.stockStatus === "Critical" ? "#fee2e2" : "#ffedd5",
                              color: row.stockStatus === "Critical" ? "#dc2626" : "#ea580c",
                              borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                            }}
                          >
                            {row.stockStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 12 }}>
                <span style={{ fontSize: 12, color: "#dc2626" }}>{criticalCount} Critical · {lowCount} Low</span>
              </div>
            </>
          )}
        </ChartCard>
      </div>
    </div>
  );
}


const assetDowntimeAssets = [
  { key: "Fan Cover Project", color: "#dc2626" },
  { key: "Climate System Unit 4", color: "#2563eb" },
  { key: "Water Pump Station 2", color: "#16a34a" },
  { key: "Generator Unit 1", color: "#d97706" },
  { key: "Feed Auger System A", color: "#7c3aed" },
  { key: "Ventilation Motor B3", color: "#0891b2" },
  { key: "Heating Element Barn 5", color: "#db2777" },
  { key: "Washer Residence 1", color: "#65a30d" },
  { key: "Dryer Residence 2", color: "#ea580c" },
  { key: "Range/Stove Residence 1", color: "#4f46e5" },
  { key: "Refrigerator Unit 3", color: "#0d9488" },
  { key: "Fan Belt Drive C", color: "#b45309" },
  { key: "Water Line Valve 4", color: "#6d28d9" },
  { key: "Electrical Panel B2", color: "#be185d" },
  { key: "Climate Controller 2", color: "#047857" },
];

const assetDowntimeMonthly = [
  { month: "Aug", "Fan Cover Project": 5, "Climate System Unit 4": 3, "Water Pump Station 2": 4, "Generator Unit 1": 2, "Feed Auger System A": 3, "Ventilation Motor B3": 2, "Heating Element Barn 5": 1, "Washer Residence 1": 2, "Dryer Residence 2": 1, "Range/Stove Residence 1": 1, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 2, "Water Line Valve 4": 1, "Electrical Panel B2": 1, "Climate Controller 2": 0 },
  { month: "Sep", "Fan Cover Project": 4, "Climate System Unit 4": 4, "Water Pump Station 2": 2, "Generator Unit 1": 3, "Feed Auger System A": 2, "Ventilation Motor B3": 3, "Heating Element Barn 5": 2, "Washer Residence 1": 1, "Dryer Residence 2": 2, "Range/Stove Residence 1": 1, "Refrigerator Unit 3": 2, "Fan Belt Drive C": 1, "Water Line Valve 4": 1, "Electrical Panel B2": 0, "Climate Controller 2": 1 },
  { month: "Oct", "Fan Cover Project": 3, "Climate System Unit 4": 2, "Water Pump Station 2": 3, "Generator Unit 1": 3, "Feed Auger System A": 2, "Ventilation Motor B3": 1, "Heating Element Barn 5": 3, "Washer Residence 1": 2, "Dryer Residence 2": 1, "Range/Stove Residence 1": 2, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 1, "Water Line Valve 4": 2, "Electrical Panel B2": 1, "Climate Controller 2": 1 },
  { month: "Nov", "Fan Cover Project": 6, "Climate System Unit 4": 5, "Water Pump Station 2": 4, "Generator Unit 1": 4, "Feed Auger System A": 3, "Ventilation Motor B3": 3, "Heating Element Barn 5": 2, "Washer Residence 1": 3, "Dryer Residence 2": 3, "Range/Stove Residence 1": 2, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 1, "Water Line Valve 4": 1, "Electrical Panel B2": 2, "Climate Controller 2": 1 },
  { month: "Dec", "Fan Cover Project": 3, "Climate System Unit 4": 3, "Water Pump Station 2": 2, "Generator Unit 1": 1, "Feed Auger System A": 2, "Ventilation Motor B3": 1, "Heating Element Barn 5": 2, "Washer Residence 1": 1, "Dryer Residence 2": 1, "Range/Stove Residence 1": 1, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 2, "Water Line Valve 4": 0, "Electrical Panel B2": 1, "Climate Controller 2": 1 },
  { month: "Jan", "Fan Cover Project": 3, "Climate System Unit 4": 2, "Water Pump Station 2": 2, "Generator Unit 1": 2, "Feed Auger System A": 2, "Ventilation Motor B3": 2, "Heating Element Barn 5": 1, "Washer Residence 1": 1, "Dryer Residence 2": 1, "Range/Stove Residence 1": 1, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 0, "Water Line Valve 4": 1, "Electrical Panel B2": 0, "Climate Controller 2": 0 },
];

const mttrMtbfData = [
  { name: "Fan Cover Project", mttr: 38.5, mtbf: 120, mtbfScaled: 12, wrs: 24 },
  { name: "Climate System Unit 4", mttr: 29.2, mtbf: 180, mtbfScaled: 18, wrs: 19 },
  { name: "Water Pump Station 2", mttr: 24.8, mtbf: 210, mtbfScaled: 21, wrs: 17 },
  { name: "Generator Unit 1", mttr: 31.4, mtbf: 150, mtbfScaled: 15, wrs: 15 },
  { name: "Feed Auger System A", mttr: 18.6, mtbf: 240, mtbfScaled: 24, wrs: 14 },
  { name: "Ventilation Motor B3", mttr: 22.1, mtbf: 280, mtbfScaled: 28, wrs: 12 },
  { name: "Heating Element Barn 5", mttr: 15.3, mtbf: 320, mtbfScaled: 32, wrs: 11 },
  { name: "Washer Residence 1", mttr: 8.4, mtbf: 420, mtbfScaled: 42, wrs: 10 },
  { name: "Dryer Residence 2", mttr: 7.9, mtbf: 400, mtbfScaled: 40, wrs: 9 },
  { name: "Range/Stove Residence 1", mttr: 5.2, mtbf: 480, mtbfScaled: 48, wrs: 8 },
  { name: "Refrigerator Unit 3", mttr: 6.8, mtbf: 460, mtbfScaled: 46, wrs: 7 },
  { name: "Fan Belt Drive C", mttr: 12.4, mtbf: 350, mtbfScaled: 35, wrs: 7 },
  { name: "Water Line Valve 4", mttr: 9.7, mtbf: 390, mtbfScaled: 39, wrs: 6 },
  { name: "Electrical Panel B2", mttr: 19.3, mtbf: 260, mtbfScaled: 26, wrs: 5 },
  { name: "Climate Controller 2", mttr: 14.6, mtbf: 310, mtbfScaled: 31, wrs: 4 },
];

function getReliabilityColor(mttr: number, mtbf: number) {
  if (mtbf < 220 && mttr > 20) return "#dc2626";
  if (mtbf > 380 && mttr < 12) return "#16a34a";
  return "#d97706";
}


function AssetDowntimeChart({ days, rawDowntimeData }: { days: number; rawDowntimeData: any[] }) {
  const dashboardApi = useDashboardApi();
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isExportingDowntimePDF, setIsExportingDowntimePDF] = useState(false);
  const [isExportingDowntimeXLS, setIsExportingDowntimeXLS] = useState(false);

  const handleExportDowntimePDF = () => {
    setIsExportingDowntimePDF(true);
    dashboardApi.exportAssetDowntimeToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'asset_downtime'); setIsExportingDowntimePDF(false); },
      () => { setIsExportingDowntimePDF(false); }
    );
  };
  const handleExportDowntimeXLS = () => {
    setIsExportingDowntimeXLS(true);
    dashboardApi.exportAssetDowntimeToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'asset_downtime'); setIsExportingDowntimeXLS(false); },
      () => { setIsExportingDowntimeXLS(false); }
    );
  };

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Build chart data from API response
  const CHART_COLORS = ["#dc2626","#2563eb","#16a34a","#d97706","#7c3aed","#0891b2","#db2777","#65a30d","#ea580c","#4f46e5","#0d9488","#b45309","#6d28d9","#be185d","#047857"];
  const assetNames = [...new Set(rawDowntimeData.map((r: any) => r.assetName))];
  const months = [...new Set(rawDowntimeData.map((r: any) => r.monthKey))].sort();
  const apiAssets = assetNames.map((name, i) => ({ key: name, color: CHART_COLORS[i % CHART_COLORS.length] }));
  const apiMonthly = months.map((mk) => {
    const label = rawDowntimeData.find((r: any) => r.monthKey === mk)?.monthLabel || mk;
    const row: any = { month: label };
    assetNames.forEach((name) => {
      const match = rawDowntimeData.find((r: any) => r.assetName === name && r.monthKey === mk);
      row[name] = match ? match.downtimeHours : 0;
    });
    return row;
  });

  const filtered = apiAssets.filter((a) => a.key.toLowerCase().includes(search.toLowerCase()));
  const visibleAssets = selectedAsset
    ? apiAssets.filter((a) => a.key === selectedAsset)
    : apiAssets;

  const periodLabel = days <= 7 ? "last 7 days" : days <= 30 ? "last 30 days" : days <= 120 ? "last 120 days" : "last 12 months";

  return (
    <ChartCard
      title="Asset Downtime"
      subtitle={selectedAsset ? `Downtime Hours — ${selectedAsset} (${periodLabel})` : `Downtime hours per asset — top 15 assets (${periodLabel})`}
      rightHeader={
        <div className="flex items-center gap-2">
        <Dropdown position="end">
          <Dropdown.Button>
            <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
              Export
            </button>
          </Dropdown.Button>
          <Dropdown.Content>
            <Menu onClick={handleExportDowntimePDF} disabled={isExportingDowntimePDF}>
              {isExportingDowntimePDF ? "Exporting PDF..." : "PDF"}
            </Menu>
            <Menu onClick={handleExportDowntimeXLS} disabled={isExportingDowntimeXLS}>
              {isExportingDowntimeXLS ? "Exporting XLS..." : "Excel"}
            </Menu>
          </Dropdown.Content>
        </Dropdown>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpen(!open)}
            style={{
              padding: "4px 10px", fontSize: 12, borderRadius: 4, cursor: "pointer",
              background: selectedAsset ? "#2B5A2E" : "white",
              color: selectedAsset ? "white" : "#6b7280",
              border: selectedAsset ? "1px solid #2B5A2E" : "1px solid #e5e7eb",
              minWidth: 160, textAlign: "left",
            }}
          >
            {selectedAsset || "Search asset..."}
          </button>
          {open && (
            <div style={{
              position: "absolute", top: "100%", right: 0, marginTop: 4, width: 240,
              background: "white", border: "1px solid #e5e7eb", borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 50,
            }}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to search..."
                autoFocus
                style={{
                  width: "100%", padding: "8px 12px", fontSize: 12, border: "none",
                  borderBottom: "1px solid #f3f4f6", outline: "none", boxSizing: "border-box",
                }}
              />
              <div style={{ maxHeight: 200, overflowY: "auto" }}>
                {selectedAsset && (
                  <button
                    onClick={() => { setSelectedAsset(null); setSearch(""); setOpen(false); }}
                    style={{
                      width: "100%", padding: "8px 12px", fontSize: 12, textAlign: "left",
                      cursor: "pointer", background: "#f9fafb", border: "none", color: "#dc2626",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    Clear selection (show all)
                  </button>
                )}
                {filtered.map((a) => (
                  <button
                    key={a.key}
                    onClick={() => { setSelectedAsset(a.key); setSearch(""); setOpen(false); }}
                    style={{
                      width: "100%", padding: "8px 12px", fontSize: 12, textAlign: "left",
                      cursor: "pointer", background: selectedAsset === a.key ? "#f0fdf4" : "white",
                      border: "none", borderBottom: "1px solid #f9fafb",
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f9fafb"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = selectedAsset === a.key ? "#f0fdf4" : "white"; }}
                  >
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: a.color }} />
                    {a.key}
                  </button>
                ))}
                {filtered.length === 0 && (
                  <p style={{ padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>No assets found</p>
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      }
    >
      <div className="flex flex-col">
        <ResponsiveContainer width="100%" height={420}>
          <LineChart data={apiMonthly} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Downtime (hrs)", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb", maxHeight: 300, overflowY: "auto" }}
              itemSorter={(item: any) => -(item.value || 0)}
            />
            {visibleAssets.map((a) => (
              <Line key={a.key} type="monotone" dataKey={a.key} stroke={a.color} strokeWidth={selectedAsset ? 3 : 2} dot={{ r: selectedAsset ? 4 : 3 }} activeDot={{ r: 5 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
          {visibleAssets.map((a) => (
            <div key={a.key} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: a.color }} />
              <span style={{ fontSize: 11, color: "#6b7280" }}>{a.key}</span>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}

function AssetMetricsTab({ days }: { days: number }) {
  const dashboardApi = useDashboardApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isExportingDownAssetsPDF, setIsExportingDownAssetsPDF] = useState(false);
  const [isExportingDownAssetsXLS, setIsExportingDownAssetsXLS] = useState(false);
  const [isExportingMttrMtbfPDF, setIsExportingMttrMtbfPDF] = useState(false);
  const [isExportingMttrMtbfXLS, setIsExportingMttrMtbfXLS] = useState(false);
  const [isExportingReliabilityPDF, setIsExportingReliabilityPDF] = useState(false);
  const [isExportingReliabilityXLS, setIsExportingReliabilityXLS] = useState(false);
  const [isExportingWarrantiesPDF, setIsExportingWarrantiesPDF] = useState(false);
  const [isExportingWarrantiesXLS, setIsExportingWarrantiesXLS] = useState(false);

  useEffect(() => {
    setLoading(true);
    dashboardApi.getManagerDashboardAssets(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setData(d);
        setLoading(false);
      },
      (error: any) => { console.error("Failed to fetch asset metrics:", error); setLoading(false); }
    );
  }, [days]);

  const handleExportDownAssetsPDF = () => {
    setIsExportingDownAssetsPDF(true);
    dashboardApi.exportDownAssetsToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'down_assets'); setIsExportingDownAssetsPDF(false); },
      () => { setIsExportingDownAssetsPDF(false); }
    );
  };
  const handleExportDownAssetsXLS = () => {
    setIsExportingDownAssetsXLS(true);
    dashboardApi.exportDownAssetsToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'down_assets'); setIsExportingDownAssetsXLS(false); },
      () => { setIsExportingDownAssetsXLS(false); }
    );
  };
  const handleExportMttrMtbfPDF = () => {
    setIsExportingMttrMtbfPDF(true);
    dashboardApi.exportMttrMtbfToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'mttr_mtbf_by_asset'); setIsExportingMttrMtbfPDF(false); },
      () => { setIsExportingMttrMtbfPDF(false); }
    );
  };
  const handleExportMttrMtbfXLS = () => {
    setIsExportingMttrMtbfXLS(true);
    dashboardApi.exportMttrMtbfToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'mttr_mtbf_by_asset'); setIsExportingMttrMtbfXLS(false); },
      () => { setIsExportingMttrMtbfXLS(false); }
    );
  };
  const handleExportReliabilityPDF = () => {
    setIsExportingReliabilityPDF(true);
    dashboardApi.exportReliabilityMatrixToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'reliability_matrix'); setIsExportingReliabilityPDF(false); },
      () => { setIsExportingReliabilityPDF(false); }
    );
  };
  const handleExportReliabilityXLS = () => {
    setIsExportingReliabilityXLS(true);
    dashboardApi.exportReliabilityMatrixToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'reliability_matrix'); setIsExportingReliabilityXLS(false); },
      () => { setIsExportingReliabilityXLS(false); }
    );
  };
  const handleExportWarrantiesPDF = () => {
    setIsExportingWarrantiesPDF(true);
    dashboardApi.exportExpiringWarrantiesToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'expiring_warranties'); setIsExportingWarrantiesPDF(false); },
      () => { setIsExportingWarrantiesPDF(false); }
    );
  };
  const handleExportWarrantiesXLS = () => {
    setIsExportingWarrantiesXLS(true);
    dashboardApi.exportExpiringWarrantiesToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'expiring_warranties'); setIsExportingWarrantiesXLS(false); },
      () => { setIsExportingWarrantiesXLS(false); }
    );
  };

  if (loading) return <WRTabLoader />;

  const downAssets = data?.downAssets || [];
  const expiringWarranties = data?.expiringWarranties || [];
  const apiMttrMtbf = data?.mttrMtbfData || [];
  const chartMttrMtbfData = apiMttrMtbf.map((d: any) => ({ ...d, mtbfScaled: (d.mtbf || 0) / 10 }));

  const avgMttr = chartMttrMtbfData.length > 0
    ? Math.round((chartMttrMtbfData.reduce((sum: number, d: any) => sum + (d.mttr || 0), 0) / chartMttrMtbfData.length) * 10) / 10
    : 0;
  const avgMtbf = chartMttrMtbfData.length > 0
    ? Math.round(chartMttrMtbfData.reduce((sum: number, d: any) => sum + (d.mtbf || 0), 0) / chartMttrMtbfData.length)
    : 0;

  const maxMtbf = Math.max(...chartMttrMtbfData.map((d: any) => d.mtbf || 0), 100);
  const maxMttr = Math.max(...chartMttrMtbfData.map((d: any) => d.mttr || 0), 10);
  const scatterXMax = Math.ceil((maxMtbf + 50) / 50) * 50;
  const scatterYMax = Math.ceil((maxMttr + 5) / 5) * 5;

  const urgencyColors: Record<string, { bg: string; color: string }> = {
    Red: { bg: "#fee2e2", color: "#dc2626" },
    Orange: { bg: "#ffedd5", color: "#ea580c" },
    Yellow: { bg: "#fef9c3", color: "#a16207" },
  };

  const redCount = expiringWarranties.filter((r: any) => r.urgencyLevel === "Red").length;
  const orangeCount = expiringWarranties.filter((r: any) => r.urgencyLevel === "Orange").length;
  const yellowCount = expiringWarranties.filter((r: any) => r.urgencyLevel === "Yellow").length;

  return (
    <div className="flex flex-col gap-5">
      <ChartCard title="Down Assets" subtitle="Assets currently not in Operational status (excluding Disposed)"
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportDownAssetsPDF} disabled={isExportingDownAssetsPDF}>
                {isExportingDownAssetsPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportDownAssetsXLS} disabled={isExportingDownAssetsXLS}>
                {isExportingDownAssetsXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        {downAssets.length === 0 ? (
          <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>All assets are operational</p>
        ) : (
          <>
            <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Asset Name", "Asset Code", "Facility", "Sub Facility", "Status", "Down Since"].map((h) => (
                      <th key={h} style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 16px", textAlign: "left", position: "sticky", top: 0 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {downAssets.map((row: any, i: number) => (
                    <tr key={row.assetID} style={{ background: i % 2 === 1 ? "#fafafa" : "white" }}>
                      <td style={{ fontSize: 13, padding: "10px 16px", color: "#111827" }}>{row.assetName}</td>
                      <td style={{ fontSize: 13, padding: "10px 16px", fontFamily: "monospace", color: "#6b7280" }}>{row.assetCode}</td>
                      <td style={{ fontSize: 13, padding: "10px 16px", color: "#111827" }}>{row.facilityName}</td>
                      <td style={{ fontSize: 13, padding: "10px 16px", color: "#111827" }}>{row.subFacilityName || "—"}</td>
                      <td style={{ fontSize: 13, padding: "10px 16px" }}>
                        <span style={{ background: row.colourCode ? `${row.colourCode}20` : "#fee2e2", color: row.colourCode || "#dc2626", borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                          {row.statusName}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, padding: "10px 16px", color: "#6b7280" }}>
                        {row.downSince ? new Date(row.downSince).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 12 }}>
              <span style={{ fontSize: 12, color: "#dc2626" }}>{downAssets.length} asset{downAssets.length !== 1 ? "s" : ""} currently not operational</span>
            </div>
          </>
        )}
      </ChartCard>

      <AssetDowntimeChart days={days} rawDowntimeData={data?.assetDowntimeData || []} />

      <ChartCard title="MTTR & MTBF by Asset" subtitle="Mean Time To Repair vs Mean Time Between Failures — top 15 assets"
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportMttrMtbfPDF} disabled={isExportingMttrMtbfPDF}>
                {isExportingMttrMtbfPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportMttrMtbfXLS} disabled={isExportingMttrMtbfXLS}>
                {isExportingMttrMtbfXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        <div className="flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" style={{ marginBottom: 24 }}>
            <div className="flex items-center gap-3" style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
              <div style={{ width: 3, alignSelf: "stretch", background: "#d97706", borderRadius: 2 }} />
              <div>
                <p style={{ fontSize: 12, color: "#6b7280" }}>Overall Avg MTTR</p>
                <p style={{ fontSize: 28, fontWeight: 600, color: "#111827" }}>{avgMttr} hrs</p>
                <p style={{ fontSize: 11, color: "#6b7280" }}>Avg time from asset Down → Closed-Complete</p>
              </div>
            </div>
            <div className="flex items-center gap-3" style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
              <div style={{ width: 3, alignSelf: "stretch", background: "#2563eb", borderRadius: 2 }} />
              <div>
                <p style={{ fontSize: 12, color: "#6b7280" }}>Overall Avg MTBF</p>
                <p style={{ fontSize: 28, fontWeight: 600, color: "#111827" }}>{avgMtbf} hrs</p>
                <p style={{ fontSize: 11, color: "#6b7280" }}>Avg time between failures across all assets</p>
              </div>
            </div>
          </div>
          <div className="flex gap-5 mb-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "#d97706" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>MTTR (hrs)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "#2563eb" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>MTBF ÷ 10 (hrs)</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={chartMttrMtbfData} margin={{ top: 5, right: 10, left: -10, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="name" tick={(props: any) => { const { x, y, payload } = props; return (<g transform={`translate(${x},${y})`}><text x={0} y={0} dy={10} textAnchor="end" fill="#6b7280" fontSize={11} transform="rotate(-45)">{payload.value}</text></g>); }} interval={0} height={80} />
              <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Hours (MTBF shown at ÷10 scale)", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const item = chartMttrMtbfData.find((d: any) => d.name === label);
                  return (
                    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                      <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                      <p style={{ color: "#d97706", margin: "2px 0" }}>MTTR: {item?.mttr} hrs</p>
                      <p style={{ color: "#2563eb", margin: "2px 0" }}>MTBF: {item?.mtbf} hrs</p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="mttr" fill="#d97706" radius={[3, 3, 0, 0]} />
              <Bar dataKey="mtbfScaled" fill="#2563eb" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <p style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>Note: MTBF values shown at ÷10 scale for visual comparison. Hover for actual values.</p>
        </div>
      </ChartCard>

      <ChartCard title="Reliability Matrix" subtitle="Asset reliability — MTBF (higher = better) vs MTTR (lower = better). Bubble size = number of WRs raised."
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportReliabilityPDF} disabled={isExportingReliabilityPDF}>
                {isExportingReliabilityPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportReliabilityXLS} disabled={isExportingReliabilityXLS}>
                {isExportingReliabilityXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        <div className="flex flex-col">
          <div className="flex gap-5 mb-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#dc2626" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>Critical assets (high MTTR, low MTBF)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#d97706" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>Watch list (moderate risk)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#16a34a" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>Reliable assets (low MTTR, high MTBF)</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid stroke="#f3f4f6" />
              <XAxis type="number" dataKey="mtbf" domain={[0, scatterXMax]} tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "MTBF (hrs) — Higher is better", position: "insideBottom", style: { fontSize: 11, fill: "#6b7280" }, offset: -5 }} />
              <YAxis type="number" dataKey="mttr" domain={[0, scatterYMax]} tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "MTTR (hrs) — Lower is better", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 0 }} />
              <ZAxis range={[80, 80]} />
              <ReferenceArea x1={0} x2={avgMtbf} y1={avgMttr} y2={scatterYMax} fill="#fee2e2" fillOpacity={0.3} label={{ value: "Problem assets", position: "insideTopLeft", fill: "#dc2626", fontSize: 11 }} />
              <ReferenceArea x1={avgMtbf} x2={scatterXMax} y1={avgMttr} y2={scatterYMax} fill="transparent" fillOpacity={0} label={{ value: "Investigate", position: "insideTopRight", fill: "#d97706", fontSize: 11 }} />
              <ReferenceArea x1={0} x2={avgMtbf} y1={0} y2={avgMttr} fill="transparent" fillOpacity={0} label={{ value: "Watch list", position: "insideBottomLeft", fill: "#d97706", fontSize: 11 }} />
              <ReferenceArea x1={avgMtbf} x2={scatterXMax} y1={0} y2={avgMttr} fill="#dcfce7" fillOpacity={0.3} label={{ value: "Ideal assets", position: "insideBottomRight", fill: "#16a34a", fontSize: 11 }} />
              <ReferenceLine x={avgMtbf} stroke="#e5e7eb" strokeDasharray="4 4" />
              <ReferenceLine y={avgMttr} stroke="#e5e7eb" strokeDasharray="4 4" />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                      <p style={{ fontWeight: 600, marginBottom: 4 }}>{d?.name}</p>
                      <p style={{ margin: "2px 0" }}>MTBF: {d?.mtbf} hrs</p>
                      <p style={{ margin: "2px 0" }}>MTTR: {d?.mttr} hrs</p>
                      <p style={{ margin: "2px 0", color: "#6b7280" }}>{d?.wrs} Work Requests</p>
                    </div>
                  );
                }}
              />
              {chartMttrMtbfData.map((d: any) => (
                <Scatter key={d.name} data={[d]} fill={getReliabilityColor(d.mttr, d.mtbf)}>
                  <Cell fill={getReliabilityColor(d.mttr, d.mtbf)} />
                </Scatter>
              ))}
            </ScatterChart>
          </ResponsiveContainer>
          <p style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>Dashed lines show overall averages (Avg MTTR: {avgMttr} hrs · Avg MTBF: {avgMtbf} hrs)</p>
        </div>
      </ChartCard>

      <ChartCard title="Expiring Warranties" subtitle="Assets with warranties expiring in the next 90 days"
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportWarrantiesPDF} disabled={isExportingWarrantiesPDF}>
                {isExportingWarrantiesPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportWarrantiesXLS} disabled={isExportingWarrantiesXLS}>
                {isExportingWarrantiesXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        {expiringWarranties.length === 0 ? (
          <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No warranties expiring in the next 90 days</p>
        ) : (
          <>
            <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Asset Name", "Asset Code", "Facility", "Warranty Expiry", "Days Left", "Urgency"].map((h) => (
                      <th key={h} style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 16px", textAlign: "left", position: "sticky", top: 0 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {expiringWarranties.map((row: any, i: number) => {
                    const uc = urgencyColors[row.urgencyLevel] || urgencyColors.Yellow;
                    return (
                      <tr key={row.assetID} style={{ background: i % 2 === 1 ? "#fafafa" : "white" }}>
                        <td style={{ fontSize: 13, padding: "10px 16px" }}>
                          <span style={{ color: "#2563eb", cursor: "pointer" }} className="hover:underline">{row.assetName}</span>
                        </td>
                        <td style={{ fontSize: 13, padding: "10px 16px", fontFamily: "monospace", color: "#6b7280" }}>{row.assetCode}</td>
                        <td style={{ fontSize: 13, padding: "10px 16px", color: "#111827" }}>{row.facilityName}</td>
                        <td style={{ fontSize: 13, padding: "10px 16px", color: uc.color, fontWeight: row.urgencyLevel === "Red" ? 600 : 400 }}>
                          {new Date(row.warrantyEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </td>
                        <td style={{ fontSize: 13, padding: "10px 16px", color: uc.color, fontWeight: 600 }}>{row.daysUntilExpiry}d</td>
                        <td style={{ fontSize: 13, padding: "10px 16px" }}>
                          <span style={{ background: uc.bg, color: uc.color, borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                            {row.urgencyLevel === "Red" ? "Urgent" : row.urgencyLevel === "Orange" ? "Soon" : "Upcoming"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 12 }}>
              <span style={{ fontSize: 12 }}>
                {redCount > 0 && <span style={{ color: "#dc2626" }}>{redCount} urgent (≤15d)</span>}
                {redCount > 0 && (orangeCount > 0 || yellowCount > 0) && <span style={{ color: "#6b7280" }}> · </span>}
                {orangeCount > 0 && <span style={{ color: "#ea580c" }}>{orangeCount} soon (≤30d)</span>}
                {orangeCount > 0 && yellowCount > 0 && <span style={{ color: "#6b7280" }}> · </span>}
                {yellowCount > 0 && <span style={{ color: "#a16207" }}>{yellowCount} upcoming (≤90d)</span>}
              </span>
            </div>
          </>
        )}
      </ChartCard>
    </div>
  );
}

const ManagerDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [timePeriod, setTimePeriod] = useState<WRTimePeriod>("30D");
  const days = wrPeriodToDays[timePeriod];

  return (
    <Layout>
      <Header title="Manager Dashboard">
        <Header.Right>
          <WRTimeToggle active={timePeriod} onChange={setTimePeriod} />
        </Header.Right>
      </Header>
      <div className="p-4">
        <div
          className="bg-white border-b shrink-0"
          style={{ borderColor: "#e5e7eb", padding: "0 24px" }}
        >
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="relative transition-colors"
                style={{
                  padding: "12px 16px",
                  fontSize: 14,
                  fontWeight: activeTab === tab ? 600 : 400,
                  color: activeTab === tab ? "#2B5A2E" : "#6b7280",
                  borderBottom: activeTab === tab ? "2px solid #2B5A2E" : "2px solid transparent",
                  background: "none",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab) e.currentTarget.style.color = "#111827";
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab) e.currentTarget.style.color = "#6b7280";
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: "#f9fafb", padding: 24, flex: 1 }}>
          {activeTab === "Work Requests & Tasks" && <WorkRequestsTab days={days} timePeriod={timePeriod} />}
          {activeTab === "Team Metrics" && <TeamMetricsTab days={days} />}
          {activeTab === "Purchasing" && <PurchasingTab days={days} />}
          {activeTab === "Asset Metrics" && <AssetMetricsTab days={days} />}
          {activeTab !== "Work Requests & Tasks" && activeTab !== "Team Metrics" && activeTab !== "Purchasing" && activeTab !== "Asset Metrics" && (
            <div
              className="bg-white rounded-lg text-center"
              style={{ padding: 40, borderRadius: 8 }}
            >
              <p style={{ color: "#9ca3af", fontSize: 14 }}>
                {activeTab} charts coming soon
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ManagerDashboard;
